import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  getPersons,
  getPerson,
  createPerson,
  updatePerson,
  deletePerson,
  type SearchParams,
  type CreatePersonDto,
  type UpdatePersonDto,
} from '@/api/persons.api';
import { ApiError } from '@/api/client';

// ─── Personenliste ──────────────────────────────────────────────────────────

export function usePersons(params?: SearchParams) {
  return useQuery({
    queryKey: QUERY_KEYS.persons.list(params as Record<string, unknown> | undefined),
    queryFn: () => getPersons(params),
  });
}

// ─── Einzelne Person ────────────────────────────────────────────────────────

export function usePerson(id: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.persons.detail(id!),
    queryFn: () => getPerson(id!),
    enabled: !!id,
  });
}

// ─── Person anlegen ─────────────────────────────────────────────────────────

export function useCreatePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePersonDto) => createPerson(dto),
    onSuccess: (person) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      toast.success(`${person.firstName} ${person.lastName ?? ''} angelegt.`);
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Person konnte nicht angelegt werden.');
    },
  });
}

// ─── Person aktualisieren ───────────────────────────────────────────────────

export function useUpdatePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePersonDto }) => updatePerson(id, dto),
    onSuccess: (person) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(person.id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      toast.success(`${person.firstName} ${person.lastName ?? ''} aktualisiert.`);
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Person konnte nicht aktualisiert werden.');
    },
  });
}

// ─── Person löschen ─────────────────────────────────────────────────────────

export function useDeletePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deletePerson(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.all });
      queryClient.removeQueries({ queryKey: QUERY_KEYS.persons.detail(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.relationships.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.familyMembers.all });
      toast.success('Person gelöscht.');
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Person konnte nicht gelöscht werden.');
    },
  });
}
